import { serializeQuickFilter } from "@/admin/filterUtils.ts";
import {
    BillingAccountOrderFields,
    ColumnFilterInputType,
    LoadBalancerOrderFields,
    ServicePackageOrderFields,
    UserOrderFields,
    VmOrderFields,
} from "@/admin/graphql.gen/graphql.ts";
import { link } from "@/components/Action.tsx";
import { isArray } from "remeda";

type SearchId = string | string[];

/** Build URL search parameters for a detail view open to {@link idField} == {@link id} */
export function buildTableDetailSearch(idField: string, id: SearchId) {
    const detailId: string = isArray(id) ? id[0] : id;
    return {
        ...buildTableExactSearch(idField, id),
        detailId,
    };
}

/** Build URL search parameters with a filter {@link field} == {@link value}. */
export function buildTableExactSearch(field: string, value: SearchId) {
    // Convert single-item arrays to a single value
    if (isArray(value) && value.length === 1) {
        value = value[0];
    }

    return {
        filters: [
            serializeQuickFilter(
                isArray(value)
                    ? {
                          field,
                          type: ColumnFilterInputType.List,
                          values: value,
                      }
                    : {
                          field,
                          type: ColumnFilterInputType.Exact,
                          value,
                      },
            ),
        ],
    };
}

export function adminBillingAccountLink(id: SearchId) {
    return link({
        to: "/admin/accounts",
        search: buildTableDetailSearch(BillingAccountOrderFields.Id, id),
    });
}

export function adminUserLink(id: SearchId) {
    return link({
        to: "/admin/users",
        search: buildTableDetailSearch(UserOrderFields.Id, id),
    });
}

/** XXX: Broken since base-operator does not support EXACT filtering on UUID fields */
export function adminVmLink(id: SearchId) {
    return link({
        to: "/admin/virtual_machines",
        search: buildTableDetailSearch(VmOrderFields.Uuid, id),
    });
}

export function adminHostLink(id: string) {
    return link({
        to: "/admin/hosts",
        // XXX: This is not a real GraphQL table so only the `detailId` is used by the actual page
        search: { detailId: id },
    });
}

export function adminServiceLink(id: SearchId) {
    return link({
        to: "/admin/service_packages",
        search: buildTableDetailSearch(ServicePackageOrderFields.Uuid, id),
    });
}

export function adminLoadBalancerLink(id: SearchId) {
    return link({
        to: "/admin/load_balancers",
        search: buildTableDetailSearch(LoadBalancerOrderFields.Uuid, id),
    });
}
